import type { UIIconProps } from '@dropbox/dig-icons';
import type { FormikContextType } from 'formik';
import type { FormikRefType } from 'hellospa/common/utils/formik';
import type { StoreShape, ThunkAction } from 'hellospa/redux/types';
import type { FeatureFlags } from 'js/sign-components/common/feature-flags';
import type { FC, MutableRefObject } from 'react';
import type { MessageDescriptor } from 'react-intl';
import type { ObjectSchema, Schema } from 'yup';
import { Actions } from './index';
import { RequestTypes } from './request-types';

// All Page's getText() functions must return an object of defineMessages()
export type PageText = {
  title?: MessageDescriptor;
  next?: MessageDescriptor;
  shortNext?: MessageDescriptor;
};

// An array of redux-thunk action callbacks
type ThunkActions = () => ThunkAction<void>;

export type OnPreviousCallback = () => Promise<unknown>;

export type TriggerStage = 'load' | 'submit' | 'done';

type TriggerComponentProps = {
  onCancel: () => void;
  onSkip: () => void;
  stage: TriggerStage;
};

export type TriggerComponent<P = {}> = FC<P & TriggerComponentProps> & {
  needsTrigger: (state: StoreShape, formik: FormikContextType<any>) => boolean;
};

export type TriggerStageComponents = {
  [key in TriggerStage]: TriggerComponent[];
};

export type TriggerCallback<TSubmit> = {
  load: () => void;
  submit: TSubmit;
  done: () => void;
};

export type WorkflowComponentProps<Values> = {
  // Called on component's Formik.onSubmit()
  onSaveActions: ThunkActions[];

  initialValues: Values;
  validationSchema: Schema<Values>;

  /**
   * Used by the Trigger hook to automatically call stage-based components
   */
  triggers: TriggerStageComponents;

  /**
   * Used by Stepper next/back buttons to trigger active Page Formik.submitForm()
   * However, <Formik innerRef> is incorrectly typed so we can't pass the ref
   * object directly. Use formikRefAdapter() to quiet TS
   */
  formRef: FormikRefType;

  /**
   * Called when the "Back" button is clicked from the place fields step
   * to trigger an autoSave of data from the Editor.
   */
  onPreviousCallbackRef?: MutableRefObject<OnPreviousCallback | null>;
};

/**
 * All Pages will be passed WorkflowComponentProps via props,
 * and must implement validates() and getText() functions
 */
export type WorkflowComponent<Values extends {} = {}> = FC<
  WorkflowComponentProps<Values>
> & {
  getText: (workflow: Workflow, state: StoreShape) => PageText;
  selectFormValues: (state: StoreShape, featureFlags: FeatureFlags) => Values;
  selectFormSchema: (
    state: StoreShape,
    featureFlags: FeatureFlags,
    // Formik's type definition also uses an any here.  I tried to make it use
    // `Values`, but if the validation is more restrictive then the types aren't
    // compatible.
    //
    // On the upload files page `Values` can be an empty array with files of any
    // status, but to continue none of the files can be errored.
  ) => ObjectSchema<any> | null;
};

/**
 * All Page definitions implement the following. Some properties will be
 * set at run-time by actions/workflow whenever a Workflow is changed or set
 */
export interface WorkflowPage {
  component: WorkflowComponent<any>;

  // icon is memoized SVG element
  icon: FC;
  uiIcon: UIIconProps['src'];
  path: string;
  // Set by actions/workflow.configure()
  text: PageText;
  onSave: ThunkActions[];
  onBack?: ThunkActions[];
  triggers: TriggerStageComponents;
  // Local override to hide the stepper.
  hideProgressStepper?: boolean;
  // decide wether or not this page shows footer
  hideFooter: boolean;
  // hides controls in stepper
  hideStepperBtnContainer?: boolean;
  hideNextBtn?: boolean;
  fullWidthStepper?: boolean;
  hideMergeFields?: boolean;
  containerClassName?: string;
  disableBack?: boolean;
}

export const defaultTrigger: TriggerStageComponents = {
  load: [],
  submit: [],
  done: [],
};

export enum WorkflowTags {
  DeepIntegration,
  DeepIntegrationSelfSign,
  DeepIntegrationRecipientsProvidedByHostPage,
  SignatureRequestEmail,
  SignatureRequest,
  SignatureRequestUnknown,
  SignatureRequestDocument,
  SignatureRequestTemplate,
  SignatureRequestFax,
  CreateTemplate,
  CreateTemplateLink,
  /**
   * @deprecated HideStepperToolbar is ignored in the new design.
   * use hideStepperBtnContainer on any pages that shouldn't show
   * the next and back buttons, like loading screens.
   */
  HideStepperToolbar,
  // I don't know what to call this. It attempts to restore the pre-rebrand
  // behavior that we had when we hide the stepper and let Editor handle its own
  // next button. Workflows with this tag use the text "Continue" or "Send".
  DEV24370,
  BulkSend,
  ShowInlinePreview,
  IntegrationOneTimeDoc,
  IntegrationCreateTemplate,
  GmailSelfSign,
  SandBoxedLeoUser,
}

export interface Workflow {
  id: WorkflowName;
  // useful for tying frontend workflow type to backend request type when
  // saving to the backend
  requestType: RequestTypes;
  tags: WorkflowTags[];
  name: string;
  title: MessageDescriptor | null;
  pages: WorkflowPage[];
}
export const FAX_NO_EDITOR = 'fax-no-editor';

export type WorkflowName =
  | 'SignatureRequestUnknown'
  | 'SignatureRequestDocument'
  | 'SelfSign'
  | 'SignatureRequestTemplate'
  | 'SignatureRequestTemplateAndDocument'
  | 'SignatureRequestFax'
  | 'SignatureRequestFaxNoEditor'
  | 'CreateTemplate'
  | 'CreateTemplateFromGallery'
  | 'CreateTemplateLink'
  | 'BulkSend'
  | 'BulkSendFixErrors'
  | 'DeepIntegration'
  | 'DeepIntegrationSelfSign'
  | 'DeepIntegrationRecipientsProvidedByHostPage'
  | 'DeepIntegrationSelfSignRecipientsProvidedByHostPage'
  | 'DeepIntegrationTemplate'
  | 'DeepIntegrationEditTemplate'
  | 'DeepIntegrationUseTemplate'
  | 'DeepIntegrationUseTemplatesMergeFields'
  | 'EmbeddedTemplate'
  | 'EmbeddedTemplateRecipient'
  | 'EmbeddedTemplateReview'
  | 'EmbeddedTemplateRecipientReview'
  | 'EmbeddedRequest'
  | 'EmbeddedRequestRecipient'
  | 'EmbeddedRequestReview'
  | 'EmbeddedRequestRecipientReview'
  | 'EmbeddedRequestWithTemplate'
  | 'EmbeddedRequestRecipientWithTemplate'
  | 'EmbeddedRequestReviewWithTemplate'
  | 'EmbeddedRequestRecipientReviewWithTemplate'
  | 'OfficeSelfSign'
  | 'Integration'
  | 'IntegrationOneTimeDoc'
  | 'IntegrationSelfSign'
  | 'IntegrationCreateTemplate'
  | 'IntegrationSendTemplate'
  | 'GmailSelfSign';

export interface SetWorkflowAction {
  type: Actions.SetWorkflow;
  payload: Workflow;
}

export type WorkflowActions = SetWorkflowAction;
